import { numberCaptions } from '@confinion/confideck-shared';
import type { EditorState } from '@tiptap/pm/state';

import { makeAutoIndexPlugin, type TAutoIndexPatch } from '../../../plugins/shared';
import { CAPTION_NODE_NAME } from '../constants';

/** Dispatch a tr with this meta to reset the gate so the next content change triggers reindexing. */
export const CAPTION_RESET_GATE_META = 'captionResetGate';

/** How many captions of each kind precede this doc. Zero unless the doc is one part of a larger one. */
export type TCaptionSeed = { figure: number; table: number };

/** Reindex whole doc (single forward pass, per-kind sequential counters). */
export const collectCaptionPatches = (state: EditorState, seed?: TCaptionSeed | null): TAutoIndexPatch[] =>
  numberCaptions(state.doc, seed && { FIGURE: seed.figure, TABLE: seed.table }).flatMap(({ pos, node, index }) =>
    node.attrs.index === index ? [] : [{ pos, attrs: node.attrs, want: index }]
  );

/**
 * `seed` is injected in `useOptions` from the large-document model, and answers with the captions
 * that precede the part the editor holds. Absent - a whole document in the editor - numbering starts
 * at zero exactly as before.
 */
const getAutoIndexCaptionsPlugin = (seed?: () => TCaptionSeed | null) =>
  makeAutoIndexPlugin({
    pluginName: 'caption-auto-index',
    nodeName: CAPTION_NODE_NAME,
    resetMeta: CAPTION_RESET_GATE_META,
    collectPatches: (state) => collectCaptionPatches(state, seed?.()),
  });

export default getAutoIndexCaptionsPlugin;
